import { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../lib/api';
import { markAcknowledgedLocally } from '../lib/rights';
import { useAuth } from '../lib/auth';

interface Props {
  open: boolean;
  onCancel: () => void;
  onAccepted: () => void;
}

// Industry-standard pre-separation attestation (Moises / Soundslice pattern).
// One-time per user; we record server-side for signed-in users and in
// localStorage for anonymous visitors.
export function RightsModal({ open, onCancel, onAccepted }: Props) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user, refresh } = useAuth();

  if (!open) return null;

  async function handleContinue() {
    setSubmitting(true);
    try {
      if (user) {
        await auth.ackRights();
        await refresh();
      }
      markAcknowledgedLocally();
      onAccepted();
    } catch (e) {
      // Server error is non-fatal; treat as best-effort and proceed locally.
      markAcknowledgedLocally();
      onAccepted();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="rights-title">
      <div className="rl-modal">
        <h3 id="rights-title">Before you separate audio</h3>
        <p>
          I confirm that I have the rights to use this audio for personal practice. Riffloop is
          intended for personal, non-commercial practice and learning. I'm responsible for ensuring
          I have the legal right to process any content I provide, whether by YouTube URL or direct
          upload. I will not redistribute the separated stems.
        </p>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px',
            background: 'var(--panel-2)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            marginBottom: 18,
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{
              marginTop: 2,
              width: 16,
              height: 16,
              accentColor: 'var(--accent-purple)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
            I agree to the terms above, and I've read the{' '}
            <Link to="/tos" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-purple)' }}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-purple)' }}>
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="rl-btn-secondary" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className="rl-btn-primary"
            disabled={!checked || submitting}
          >
            {submitting ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
